import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  Dropdown,
  IDropdownOption,
  mergeStyleSets,
} from "@fluentui/react";
import { CaretLeft, Robot, Leaf, MapPin, Calendar, LockSimple } from "@phosphor-icons/react";
import IconBadge from "../components/common/IconBadge";
import { getPhosphorIcon } from "../utils/iconMap";
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { boroondaraPalette } from "../theme/boroondaraTheme";
import { accessService } from "../services/accessService";
import { useNavigate } from "react-router-dom";

// ── Types ─────────────────────────────────────────
interface Sa2Area { sa2_code: string; sa2_name: string; area_sqkm: number }

// ── Constants ─────────────────────────────────────────
const FY_OPTIONS: IDropdownOption[] = [
  { key: "2024-25", text: "2024-25" },
  { key: "2023-24", text: "2023-24" },
  { key: "2022-23", text: "2022-23" },
];

const tooltipStyle = { backgroundColor: "#fff", border: "1px solid #E1E1E1", borderRadius: 4, color: "#232323", fontSize: 12 };

// ── Helpers ───────────────────────────────────────────
function fmtNum(v: number | string | null | undefined): string {
  if (v == null) return "-";
  const n = Number(v);
  return isNaN(n) ? "-" : n.toLocaleString("en-AU");
}
function truncLabel(s: string, max = 18): string {
  return s.length > max ? s.slice(0, max) + "..." : s;
}
function fmtAxis(value: number): string {
  if (Math.abs(value) >= 1_000_000_000) return (value / 1_000_000_000).toFixed(1) + "B";
  if (Math.abs(value) >= 1_000_000) return (value / 1_000_000).toFixed(1) + "M";
  if (Math.abs(value) >= 1_000) return (value / 1_000).toFixed(0) + "K";
  return String(value);
}

// ── Styles ───────────────────────────────────────────
const p = boroondaraPalette;
const styles = mergeStyleSets({
  page: { padding: 32, minHeight: "calc(100vh - 52px)", background: "#F5F5F5", "@media (max-width: 768px)": { padding: 16 } } as any,
  container: { maxWidth: 1280, margin: "0 auto" },
  headerRow: { marginBottom: 24 },
  title: { color: p.text, fontWeight: 800 as any, fontSize: 28, letterSpacing: "-0.02em" },
  subtitle: { color: p.text2, marginTop: 6, maxWidth: 780, lineHeight: "1.6", fontSize: 14 },
  chipRow: { display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" } as any,
  chip: {
    display: "inline-flex", alignItems: "center", gap: 5,
    background: "#fff", border: "1px solid #E1E1E1", borderRadius: 16,
    padding: "4px 12px", fontSize: 12, color: p.text2,
  },
  filterRow: {
    display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap", alignItems: "flex-end",
    background: "#fff", borderRadius: 8, padding: "16px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  } as any,
  filterLabel: { fontSize: 12, fontWeight: 600, color: p.text2, marginBottom: 4 },
  kpiGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 16, marginBottom: 28,
    "@media (max-width: 768px)": { gridTemplateColumns: "repeat(2, 1fr)" },
  } as any,
  kpiCard: {
    background: "#fff", borderRadius: 10, padding: "20px 18px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)", display: "flex", flexDirection: "column", gap: 4,
  } as any,
  kpiLabel: { fontSize: 12, color: p.text2, fontWeight: 600 },
  kpiValue: { fontSize: 24, fontWeight: 800 as any, color: p.text },
  kpiSub: { fontSize: 11, color: p.text3 },
  chartsRow: {
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 28,
    "@media (max-width: 900px)": { gridTemplateColumns: "1fr" },
  } as any,
  chartCard: {
    background: "#fff", borderRadius: 10, padding: 20,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  chartTitle: { fontSize: 14, fontWeight: 700, color: p.text, marginBottom: 12 },
  tableWrap: {
    background: "#fff", borderRadius: 10, padding: 20, marginBottom: 28,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflowX: "auto",
  } as any,
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 } as any,
  th: { textAlign: "left" as any, padding: "8px 10px", borderBottom: "2px solid #E1E1E1", color: p.text2, fontWeight: 600, fontSize: 12, whiteSpace: "nowrap" as any },
  td: { padding: "7px 10px", borderBottom: "1px solid #F0F0F0", color: p.text, whiteSpace: "nowrap" as any },
  agentCta: {
    background: "linear-gradient(135deg, #0D9488, #2DD4BF)", borderRadius: 12, padding: "24px 28px",
    display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer",
    transition: "transform 0.15s ease", ":hover": { transform: "translateY(-2px)" },
  } as any,
  agentCtaText: { color: "#fff" },
  agentCtaTitle: { fontSize: 18, fontWeight: 700, marginBottom: 4 },
  agentCtaSub: { fontSize: 13, opacity: 0.85 },
  agentCtaIcon: { fontSize: 32, color: "#fff", opacity: 0.9 },
  error: { marginBottom: 20 },
});

// ══════════════════════════════════════════════════════
// Component
// ══════════════════════════════════════════════════════
export default function Sustainability() {
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mainData, setMainData] = useState<any[]>([]);
  const [sa2Areas, setSa2Areas] = useState<Sa2Area[]>([]);
  const [comparisonData, setComparisonData] = useState<any[]>([]);
  const [trendData, setTrendData] = useState<any[]>([]);

  const [fy, setFy] = useState("2024-25");
  const [sa2, setSa2] = useState("all");
  const [facility, setFacility] = useState("all");

  // ── Fetch data ──
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      params.set("fy", fy);
      if (sa2 !== "all") params.set("sa2", sa2);
      if (facility !== "all") params.set("facility", facility);
      const [mainRes, sa2Res, compRes, trendRes] = await Promise.all([
        accessService.get(`/sustainability/metrics?${params}`),
        accessService.get("/abs/sa2-areas"),
        accessService.get(`/sustainability/comparison?fy=${fy}&metric=water_kl`),
        accessService.get(`/sustainability/trend?metric=emissions_tco2e`),
      ]);
      setMainData(mainRes.data);
      setSa2Areas(sa2Res.data);
      setComparisonData(compRes.data);
      setTrendData(trendRes.data);
    } catch (err: any) {
      setError(err.message || "Failed to load sustainability data");
    } finally {
      setLoading(false);
    }
  }, [fy, sa2, facility]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Dropdown options ──
  const sa2Options: IDropdownOption[] = useMemo(() => [
    { key: "all", text: "All Boroondara" },
    ...sa2Areas.map(a => ({ key: a.sa2_code, text: a.sa2_name })),
  ], [sa2Areas]);

  const facilityOptions: IDropdownOption[] = useMemo(() => {
    const unique = Array.from(new Set(mainData.map((r: any) => r.facility_name).filter(Boolean))).sort();
    return [
      { key: "all", text: "All Facilities" },
      ...unique.map(f => ({ key: f, text: f })),
    ];
  }, [mainData]);

  // ── KPIs ──
  const kpis = useMemo(() => {
    if (mainData.length === 0) return null;
    const totalEnergy = mainData.reduce((s: number, r: any) => s + (Number(r.energy_kwh) || 0), 0);
    const totalEmissions = mainData.reduce((s: number, r: any) => s + (Number(r.emissions_tco2e) || 0), 0);
    const totalWater = mainData.reduce((s: number, r: any) => s + (Number(r.water_kl) || 0), 0);
    const totalSolar = mainData.reduce((s: number, r: any) => s + (Number(r.solar_generation_kwh) || 0), 0);
    return { totalEnergy, totalEmissions, totalWater, totalSolar };
  }, [mainData]);

  const kpiCards = useMemo(() => {
    if (!kpis) return [];
    return [
      { icon: "LightningBolt", color: "#0D9488", label: "Total Energy", value: fmtNum(kpis.totalEnergy) + " kWh", sub: `FY ${fy}` },
      { icon: "Cloud", color: "#DC2626", label: "Emissions", value: fmtNum(kpis.totalEmissions) + " tCO2e", sub: `FY ${fy}` },
      { icon: "Drop", color: "#2563EB", label: "Water Usage", value: fmtNum(kpis.totalWater) + " kL", sub: `FY ${fy}` },
      { icon: "Sunny", color: "#F59E0B", label: "Solar Generation", value: fmtNum(kpis.totalSolar) + " kWh", sub: `FY ${fy}` },
    ];
  }, [kpis, fy]);

  // ── Chart data ──
  const energyByFacility = useMemo(() => {
    const byFac: Record<string, number> = {};
    mainData.forEach((r: any) => {
      const name = r.facility_name || "Unknown";
      byFac[name] = (byFac[name] || 0) + (Number(r.energy_kwh) || 0);
    });
    return Object.entries(byFac)
      .map(([name, value]) => ({ name, value }))
      .filter(d => d.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [mainData]);

  const emissionsTrend = useMemo(() => {
    return trendData.map((r: any) => ({ financial_year: r.financial_year, value: Number(r.value || r.emissions_tco2e) || 0 }));
  }, [trendData]);

  const waterBySa2 = useMemo(() => {
    return comparisonData
      .map((r: any) => ({ name: r.label || r.sa2_name, value: Number(r.value || r.water_kl) || 0 }))
      .sort((a: any, b: any) => b.value - a.value);
  }, [comparisonData]);

  const energyMix = useMemo(() => {
    const totalEnergy = mainData.reduce((s: number, r: any) => s + (Number(r.energy_kwh) || 0), 0);
    const totalSolar = mainData.reduce((s: number, r: any) => s + (Number(r.solar_generation_kwh) || 0), 0);
    const gridEnergy = totalEnergy - totalSolar;
    const data = [];
    if (gridEnergy > 0) data.push({ name: "Grid Energy", value: gridEnergy });
    if (totalSolar > 0) data.push({ name: "Solar Generation", value: totalSolar });
    return data;
  }, [mainData]);

  // ══════════════════════════════════════════════════
  // Render
  // ══════════════════════════════════════════════════
  if (loading && mainData.length === 0) {
    return (
      <div className={styles.page}>
        <div style={{ display: "flex", justifyContent: "center", paddingTop: 120 }}>
          <Spinner size={SpinnerSize.large} label="Loading sustainability data..." />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>

        {/* ── Breadcrumb ── */}
        <div style={{ marginBottom: 12 }}>
          <button
            onClick={() => nav('/dashboards')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 13, fontWeight: 600, color: '#0D9488', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <CaretLeft size={10} weight="bold" /> Dashboards
          </button>
        </div>

        {/* ── Header ── */}
        <div className={styles.headerRow}>
          <div className={styles.title}>Sustainability</div>
          <div className={styles.subtitle}>
            Energy consumption, emissions, water usage and solar generation across council facilities.
          </div>
          <div className={styles.chipRow}>
            <span className={styles.chip}><Leaf size={12} weight="regular" /> Sustainability</span>
            <span className={styles.chip}><MapPin size={12} weight="regular" /> {sa2Areas.length} SA2 Areas</span>
            <span className={styles.chip}><Calendar size={12} weight="regular" /> FY 2022-23 to 2024-25</span>
            <span className={styles.chip}><LockSimple size={12} weight="regular" /> OFFICIAL: Sensitive</span>
          </div>
        </div>

        {error && (
          <MessageBar messageBarType={MessageBarType.error} className={styles.error}>{error}</MessageBar>
        )}

        {/* ── Filters ── */}
        <div className={styles.filterRow}>
          <div>
            <div className={styles.filterLabel}>Financial Year</div>
            <Dropdown
              selectedKey={fy}
              options={FY_OPTIONS}
              onChange={(_, opt) => opt && setFy(String(opt.key))}
              styles={{ root: { width: 160 } }}
            />
          </div>
          <div>
            <div className={styles.filterLabel}>SA2 Area</div>
            <Dropdown
              selectedKey={sa2}
              options={sa2Options}
              onChange={(_, opt) => opt && setSa2(String(opt.key))}
              styles={{ root: { width: 220 } }}
            />
          </div>
          <div>
            <div className={styles.filterLabel}>Facility</div>
            <Dropdown
              selectedKey={facility}
              options={facilityOptions}
              onChange={(_, opt) => opt && setFacility(String(opt.key))}
              styles={{ root: { width: 220 } }}
            />
          </div>
        </div>

        {/* ── KPI Cards ── */}
        <div className={styles.kpiGrid}>
          {kpiCards.map((kpi, i) => (
            <div key={i} className={styles.kpiCard}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <IconBadge icon={getPhosphorIcon(kpi.icon)} size="sm" color={kpi.color} weight="fill" />
                <span className={styles.kpiLabel}>{kpi.label}</span>
              </div>
              <div className={styles.kpiValue} style={{ color: kpi.color }}>{kpi.value}</div>
              <div className={styles.kpiSub}>{kpi.sub}</div>
            </div>
          ))}
        </div>

        {/* ── Charts ── */}
        <div className={styles.chartsRow}>
          {/* Bar: Energy by Facility */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>Energy Consumption by Facility ({fy})</div>
            {energyByFacility.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={energyByFacility} margin={{ top: 5, right: 10, left: 10, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" interval={0}
                    tickFormatter={(v: string) => truncLabel(v, 14)} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAxis} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtNum(v) + " kWh"} />
                  <Bar dataKey="value" name="Energy (kWh)" fill="#0D9488" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No energy data available
              </div>
            )}
          </div>

          {/* Line: Emissions Trend */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>Emissions Trend</div>
            {emissionsTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={emissionsTrend} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis dataKey="financial_year" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAxis} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtNum(v) + " tCO2e"} />
                  <Line type="monotone" dataKey="value" name="Emissions (tCO2e)" stroke="#DC2626" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No trend data available
              </div>
            )}
          </div>
        </div>

        <div className={styles.chartsRow}>
          {/* Bar: Water by SA2 */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>Water Usage by SA2 ({fy})</div>
            {waterBySa2.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={waterBySa2} margin={{ top: 5, right: 10, left: 10, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" interval={0}
                    tickFormatter={(v: string) => truncLabel(v, 14)} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAxis} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtNum(v) + " kL"} />
                  <Bar dataKey="value" name="Water (kL)" fill="#2563EB" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No water data available
              </div>
            )}
          </div>

          {/* Pie: Energy Mix */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>Energy Mix ({fy})</div>
            {energyMix.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie data={energyMix} dataKey="value" nameKey="name" cx="50%" cy="50%"
                    outerRadius={120} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={true} fontSize={11}>
                    {energyMix.map((_, i) => (
                      <Cell key={i} fill={i === 0 ? "#6366F1" : "#F59E0B"} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtNum(v) + " kWh"} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No energy mix data available
              </div>
            )}
          </div>
        </div>

        {/* ── Data Table ── */}
        <div className={styles.tableWrap}>
          <div className={styles.chartTitle}>Sustainability Data — FY {fy}</div>
          {mainData.length > 0 ? (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>Facility</th>
                  <th className={styles.th}>Suburb</th>
                  <th className={styles.th}>Energy kWh</th>
                  <th className={styles.th}>Gas GJ</th>
                  <th className={styles.th}>Water kL</th>
                  <th className={styles.th}>Emissions</th>
                  <th className={styles.th}>Solar kWh</th>
                  <th className={styles.th}>FY</th>
                  <th className={styles.th}>Quarter</th>
                </tr>
              </thead>
              <tbody>
                {mainData.map((row: any, i: number) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? "#FAFAFA" : "#fff" }}>
                    <td className={styles.td}>{row.facility_name}</td>
                    <td className={styles.td}>{row.sa2_name}</td>
                    <td className={styles.td}>{fmtNum(row.energy_kwh)}</td>
                    <td className={styles.td}>{fmtNum(row.gas_gj)}</td>
                    <td className={styles.td}>{fmtNum(row.water_kl)}</td>
                    <td className={styles.td}>{fmtNum(row.emissions_tco2e)}</td>
                    <td className={styles.td}>{fmtNum(row.solar_generation_kwh)}</td>
                    <td className={styles.td}>{row.financial_year}</td>
                    <td className={styles.td}>{row.quarter}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: 40, textAlign: "center", color: p.text3 }}>
              No sustainability data available for the selected filters.
            </div>
          )}
        </div>

        {/* ── Agent CTA ── */}
        <div className={styles.agentCta} onClick={() => nav("/chat")} role="button" tabIndex={0}>
          <div className={styles.agentCtaText}>
            <div className={styles.agentCtaTitle}>Ask the Sustainability Agent</div>
            <div className={styles.agentCtaSub}>
              Get instant answers about energy consumption, emissions, water usage and solar generation across council facilities.
            </div>
          </div>
          <Robot size={32} weight="duotone" color="#fff" style={{ opacity: 0.9 }} />
        </div>
      </div>
    </div>
  );
}
